'use client';

import { useMemo, useState } from 'react';

import {
  useDeleteServiceGroupMutation,
  useDeleteServiceTypeMutation,
  useGetServiceGroupsQuery,
  useGetServiceTypesQuery,
  useUnarchiveServiceTypeMutation,
} from '@/features/service-types/api/service-typesApi';
import { canWorkspaceAction } from '@/features/workspaces/selectors/permissions';
import { useAppSelector } from '@/store/hooks';
import { EmptyState, InfiniteTable, Modal, OptionsMenu, Badge } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { getBillableRateAmount } from '../utils/billableRate';

import { ServiceGroupModalForm } from './ServiceGroupModalForm';
import { ServiceTypeModalForm } from './ServiceTypeModalForm';
import styles from './SettingsServiceTypesPanel.module.scss';

import type { ServiceGroup, ServiceType } from '@web/types';

type ServiceTypesScope = 'service-types' | 'service-groups';
type ServiceTypesStatusFilter = 'active' | 'inactive' | 'all';

type SettingsServiceTypesPanelProps = {
  scope: ServiceTypesScope;
  search: string;
  statusFilter: ServiceTypesStatusFilter;
  serviceGroupFilter: string;
};

function toStatusLabel(isArchived: boolean) {
  return isArchived ? 'Inactive' : 'Active';
}

function toStatusTone(isArchived: boolean) {
  return isArchived ? 'warning' : 'success';
}

function formatBillableRate(serviceType: ServiceType) {
  if (!serviceType.is_billable) {
    return '-';
  }

  const amount = getBillableRateAmount(serviceType.billable_rate) ?? 0;

  if (Number.isNaN(amount)) {
    return '$0';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);
}

function matchesStatusFilter(
  isArchived: boolean,
  statusFilter: ServiceTypesStatusFilter,
) {
  if (statusFilter === 'all') {
    return true;
  }

  if (statusFilter === 'inactive') {
    return isArchived;
  }

  return !isArchived;
}

function matchesSearch(value: string, search: string) {
  if (!search.trim()) {
    return true;
  }

  return value.toLowerCase().includes(search.trim().toLowerCase());
}

export function SettingsServiceTypesPanel({
  scope,
  search,
  statusFilter,
  serviceGroupFilter,
}: SettingsServiceTypesPanelProps) {
  const currentWorkspace = useAppSelector(
    (state) => state.workspaces.currentWorkspace,
  );
  const canEditServiceTypes = canWorkspaceAction(
    currentWorkspace,
    'service_type_management',
    'edit',
    true,
  );
  const canDeleteServiceTypes = canWorkspaceAction(
    currentWorkspace,
    'service_type_management',
    'delete',
    true,
  );

  const [editingServiceType, setEditingServiceType] =
    useState<ServiceType | null>(null);
  const [editingServiceGroup, setEditingServiceGroup] =
    useState<ServiceGroup | null>(null);

  const shouldShowArchived = statusFilter !== 'active';
  const selectedServiceGroupId =
    serviceGroupFilter !== 'all' && serviceGroupFilter !== 'ungrouped'
      ? serviceGroupFilter
      : undefined;

  const { data: serviceTypesResponse, isFetching: isServiceTypesFetching } =
    useGetServiceTypesQuery({
      page_size: 100,
      query: search || undefined,
      show_archived: shouldShowArchived,
      service_group_id: selectedServiceGroupId,
    });
  const { data: serviceGroupsResponse, isFetching: isServiceGroupsFetching } =
    useGetServiceGroupsQuery({
      page_size: 100,
      include_count: true,
      show_archived: shouldShowArchived,
    });

  const [deleteServiceType, { isLoading: isDeletingServiceType }] =
    useDeleteServiceTypeMutation();
  const [unarchiveServiceType, { isLoading: isUnarchivingServiceType }] =
    useUnarchiveServiceTypeMutation();
  const [deleteServiceGroup, { isLoading: isDeletingServiceGroup }] =
    useDeleteServiceGroupMutation();

  const serviceGroups = useMemo(
    () => serviceGroupsResponse?.data?.results ?? [],
    [serviceGroupsResponse?.data?.results],
  );
  const serviceTypeRowsRaw = useMemo(
    () => serviceTypesResponse?.data?.results ?? [],
    [serviceTypesResponse?.data?.results],
  );

  const serviceGroupNameById = useMemo(
    () =>
      new Map(serviceGroups.map((group) => [group.id, group.name] as const)),
    [serviceGroups],
  );

  const serviceTypeRows = useMemo(
    () =>
      serviceTypeRowsRaw.filter((serviceType) => {
        if (!matchesStatusFilter(serviceType.is_archived, statusFilter)) {
          return false;
        }

        if (
          serviceGroupFilter === 'ungrouped' &&
          serviceType.service_group_id !== null
        ) {
          return false;
        }

        if (selectedServiceGroupId) {
          return serviceType.service_group_id === selectedServiceGroupId;
        }

        return true;
      }),
    [
      serviceTypeRowsRaw,
      serviceGroupFilter,
      selectedServiceGroupId,
      statusFilter,
    ],
  );

  const serviceGroupRows = useMemo(
    () =>
      serviceGroups.filter((group) => {
        if (!matchesStatusFilter(group.is_archived, statusFilter)) {
          return false;
        }

        return matchesSearch(
          `${group.name} ${group.description ?? ''}`,
          search,
        );
      }),
    [search, serviceGroups, statusFilter],
  );

  const handleToggleServiceTypeArchive = async (serviceType: ServiceType) => {
    try {
      if (serviceType.is_archived) {
        await unarchiveServiceType({ serviceTypeId: serviceType.id }).unwrap();
        notify({ type: 'success', message: 'Service type activated.' });
      } else {
        await deleteServiceType({ serviceTypeId: serviceType.id }).unwrap();
        notify({ type: 'success', message: 'Service type deactivated.' });
      }
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleDeleteServiceType = async (serviceType: ServiceType) => {
    try {
      await deleteServiceType({
        serviceTypeId: serviceType.id,
        params: { hard_delete: true },
      }).unwrap();
      notify({ type: 'success', message: 'Service type deleted.' });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleDeleteGroup = async (group: ServiceGroup) => {
    try {
      await deleteServiceGroup({ serviceGroupId: group.id }).unwrap();
      notify({ type: 'success', message: 'Service group deactivated.' });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const serviceTypesColumns = [
    {
      id: 'name',
      header: 'Name',
      accessor: (row: ServiceType) => row.label,
      sortable: true,
      cell: (row: ServiceType) => (
        <span className={styles.serviceTypesPanel__nameCell}>{row.label}</span>
      ),
    },
    {
      id: 'service_group',
      header: 'Service Group',
      accessor: (row: ServiceType) =>
        row.service_group_id
          ? (serviceGroupNameById.get(row.service_group_id) ?? '-')
          : '-',
      sortable: true,
    },
    {
      id: 'billable',
      header: 'Billable',
      accessor: (row: ServiceType) => (row.is_billable ? 'Yes' : 'No'),
      sortable: true,
    },
    {
      id: 'base_rate',
      header: 'Base Rate',
      accessor: (row: ServiceType) =>
        getBillableRateAmount(row.billable_rate) ?? 0,
      cell: (row: ServiceType) => formatBillableRate(row),
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row: ServiceType) => toStatusLabel(row.is_archived),
      sortable: true,
      cell: (row: ServiceType) => (
        <span className={styles.serviceTypesPanel__statusCell}>
          <Badge
            label={toStatusLabel(row.is_archived)}
            tone={toStatusTone(row.is_archived)}
          />
        </span>
      ),
    },
    ...(canEditServiceTypes || canDeleteServiceTypes
      ? [
          {
            id: 'actions',
            header: '',
            width: 72,
            accessor: (row: ServiceType) => row.id,
            sortable: false,
            cell: (row: ServiceType) => {
              const actions = [
                ...(canEditServiceTypes
                  ? [
                      {
                        key: `edit-${row.id}`,
                        label: 'Edit',
                        onSelect: () => setEditingServiceType(row),
                      },
                    ]
                  : []),
                ...(canEditServiceTypes
                  ? [
                      {
                        key: `toggle-${row.id}`,
                        label: row.is_archived ? 'Activate' : 'Deactivate',
                        onSelect: () => {
                          void handleToggleServiceTypeArchive(row);
                        },
                        loading:
                          isDeletingServiceType || isUnarchivingServiceType,
                      },
                    ]
                  : []),
                ...(canDeleteServiceTypes
                  ? [
                      {
                        key: `delete-${row.id}`,
                        label: 'Delete',
                        tone: 'danger' as const,
                        onSelect: () => {
                          void handleDeleteServiceType(row);
                        },
                        loading: isDeletingServiceType,
                      },
                    ]
                  : []),
              ];

              if (!actions.length) {
                return null;
              }

              return (
                <div className={styles.serviceTypesPanel__actionsCell}>
                  <OptionsMenu
                    ariaLabel="Service type actions"
                    actions={actions}
                  />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const serviceGroupsColumns = [
    {
      id: 'name',
      header: 'Name',
      accessor: (row: ServiceGroup) => row.name,
      sortable: true,
      cell: (row: ServiceGroup) => (
        <span className={styles.serviceTypesPanel__nameCell}>{row.name}</span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      width: 280,
      accessor: (row: ServiceGroup) => row.description || '-',
      sortable: true,
      cell: (row: ServiceGroup) => (
        <span className={styles.serviceTypesPanel__muted}>
          {row.description || '-'}
        </span>
      ),
    },
    {
      id: 'service_type_count',
      header: 'Service types',
      width: 150,
      accessor: (row: ServiceGroup) => row.service_type_count ?? 0,
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row: ServiceGroup) => toStatusLabel(row.is_archived),
      sortable: true,
      cell: (row: ServiceGroup) => (
        <span className={styles.serviceTypesPanel__statusCell}>
          <Badge
            label={toStatusLabel(row.is_archived)}
            tone={toStatusTone(row.is_archived)}
          />
        </span>
      ),
    },
    ...(canEditServiceTypes || canDeleteServiceTypes
      ? [
          {
            id: 'actions',
            header: '',
            width: 72,
            accessor: (row: ServiceGroup) => row.id,
            sortable: false,
            cell: (row: ServiceGroup) => {
              const actions = [
                ...(canEditServiceTypes
                  ? [
                      {
                        key: `edit-group-${row.id}`,
                        label: 'Edit',
                        onSelect: () => setEditingServiceGroup(row),
                      },
                    ]
                  : []),
                ...(canDeleteServiceTypes
                  ? [
                      {
                        key: `delete-group-${row.id}`,
                        label: 'Deactivate',
                        tone: 'danger' as const,
                        onSelect: () => {
                          void handleDeleteGroup(row);
                        },
                        loading: isDeletingServiceGroup,
                      },
                    ]
                  : []),
              ];

              if (!actions.length) {
                return null;
              }

              return (
                <div className={styles.serviceTypesPanel__actionsCell}>
                  <OptionsMenu
                    ariaLabel="Service group actions"
                    actions={actions}
                  />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className={styles.serviceTypesPanel}>
      {scope === 'service-types' ? (
        !isServiceTypesFetching && serviceTypeRows.length === 0 ? (
          <EmptyState title="No service types found." />
        ) : (
          <InfiniteTable<ServiceType>
            key="service-types-table"
            tableId="settings-service-types-table"
            columns={serviceTypesColumns}
            rows={serviceTypeRows}
            getRowId={(row) => row.id}
            onLoadMore={() => {}}
            hasMore={false}
            isLoading={isServiceTypesFetching}
            virtualizedHeight="auto"
          />
        )
      ) : !isServiceGroupsFetching && serviceGroupRows.length === 0 ? (
        <EmptyState title="No service groups found." />
      ) : (
        <InfiniteTable<ServiceGroup>
          key="service-groups-table"
          tableId="settings-service-groups-table"
          columns={serviceGroupsColumns}
          rows={serviceGroupRows}
          getRowId={(row) => row.id}
          onLoadMore={() => {}}
          hasMore={false}
          isLoading={isServiceGroupsFetching}
          virtualizedHeight="auto"
        />
      )}

      <Modal
        open={Boolean(editingServiceType)}
        onClose={() => setEditingServiceType(null)}
        width="min(720px, 96vw)"
      >
        <div className={`card-container-glass ${styles.modalCard}`}>
          <h3 className={styles.modalCard__title}>Edit service type</h3>
          <p className={styles.modalCard__subtitle}>
            {editingServiceType?.label ?? ''}
          </p>
          <ServiceTypeModalForm
            mode="edit"
            serviceType={editingServiceType}
            onCancel={() => setEditingServiceType(null)}
            onSuccess={() => setEditingServiceType(null)}
          />
        </div>
      </Modal>

      <Modal
        open={Boolean(editingServiceGroup)}
        onClose={() => setEditingServiceGroup(null)}
        width="min(720px, 96vw)"
      >
        <div className={`card-container-glass ${styles.modalCard}`}>
          <h3 className={styles.modalCard__title}>Edit service group</h3>
          <p className={styles.modalCard__subtitle}>
            {editingServiceGroup?.name ?? ''}
          </p>
          <ServiceGroupModalForm
            mode="edit"
            serviceGroup={editingServiceGroup}
            onCancel={() => setEditingServiceGroup(null)}
            onSuccess={() => setEditingServiceGroup(null)}
          />
        </div>
      </Modal>
    </div>
  );
}
